import React from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchBookings } from "../../api/bookings";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  SortingState,
  ColumnFiltersState,
} from "@tanstack/react-table";

type Booking = {
  id: string;
  name: string;
  email: string;
  startDate: string;
  endDate: string;
  hash: string;
  bookingReference: string;
  guestRequests: {
    id: string;
    options: string[];
    message: string;
    createdAt: string;
  }[];
};

export default function AdminBookings() {
  const {
    data = [],
    isLoading,
    error,
  } = useQuery<Booking[]>({
    queryKey: ["bookings"],
    queryFn: fetchBookings,
  });

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );

  const [resendStatus, setResendStatus] = React.useState<
    Record<string, "idle" | "sending" | "done" | "error">
  >({});

  const columns: ColumnDef<Booking>[] = [
    {
      header: "Reference",
      accessorKey: "bookingReference",
      cell: ({ row }) => (
        <span className="text-sm text-gray-700">
          {row.original.bookingReference || (
            <em className="text-gray-400">—</em>
          )}
        </span>
      ),
    },
    {
      header: "Guest",
      accessorKey: "name",
      enableSorting: true,
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.name}</div>
          <div className="text-sm text-gray-500">{row.original.email}</div>
        </div>
      ),
    },
    {
      header: "Check-in Date",
      accessorKey: "startDate",
      enableSorting: true,
      cell: ({ row }) => (
        <span className="text-sm">
          {new Date(row.original.startDate).toLocaleDateString()}
        </span>
      ),
    },
    {
      header: "Check-out Date",
      accessorKey: "endDate",
      enableSorting: true,
      cell: ({ row }) => (
        <span className="text-sm">
          {new Date(row.original.endDate).toLocaleDateString()}
        </span>
      ),
    },
    {
      header: "Guest Link",
      cell: ({ row }) => {
        const guestUrl = `${window.location.origin}/guest/${row.original.hash}`;

        const handlePrintQR = () => {
          const qrHtml = `
            <html>
              <head>
                <title>Print QR</title>
                <style>
                  body {
                    margin: 0;
                    padding: 40px;
                    font-family: Arial, sans-serif;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 24px;
                  }
                  p {
                    font-size: 14px;
                    color: #333;
                    text-align: center;
                    max-width: 300px;
                  }
                </style>
                <script src="https://cdnjs.cloudflare.com/ajax/libs/qrious/4.0.2/qrious.min.js"></script>
              </head>
              <body>
                <canvas id="qr"></canvas>
                <p>
                  Scan this QR code to access your booking on Guest Portal.<br />
                  From there, you can request additional services or cleaning during your stay.
                </p>
                <script>
                  new QRious({
                    element: document.getElementById('qr'),
                    value: '${guestUrl}',
                    size: 256
                  });
                </script>
              </body>
            </html>
          `;

          const printWindow = window.open("", "_blank", "width=400,height=500");
          if (printWindow) {
            printWindow.document.write(qrHtml);
            printWindow.document.close();
            printWindow.focus();
          }
        };

        return (
          <div className="flex flex-col gap-2">
            <Link
              to={`/guest/${row.original.hash}`}
              target="_blank"
              className="text-blue-600 underline text-sm"
            >
              Open Link
            </Link>
            <div className="cursor-pointer" onClick={handlePrintQR}>
              <QRCodeCanvas value={guestUrl} size={64} level="H" />
            </div>
          </div>
        );
      },
    },
    {
      header: "Guest Requests",
      accessorFn: (row) => row.guestRequests.length,
      enableSorting: true,
      cell: ({ row }) =>
        row.original.guestRequests.length === 0 ? (
          <div className="text-gray-500 italic text-sm">none</div>
        ) : (
          <ul className="space-y-1 text-sm">
            {row.original.guestRequests.map((req) => (
              <li key={req.id}>
                <div className="font-medium">{req.message}</div>
                {req.options?.length > 0 && (
                  <div className="text-xs text-gray-600">
                    Services: {req.options.join(", ")}
                  </div>
                )}
                <div className="text-gray-400 text-xs">
                  {new Date(req.createdAt).toLocaleString()}
                </div>
              </li>
            ))}
          </ul>
        ),
    },
    {
      header: "Actions",
      cell: ({ row }) => {
        const bookingId = row.original.id;
        const status = resendStatus[bookingId] ?? "idle";

        const resend = async () => {
          setResendStatus((prev) => ({ ...prev, [bookingId]: "sending" }));
          try {
            const res = await fetch(
              `${process.env.REACT_APP_API_URL}/bookings/${bookingId}/resend-email`,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${localStorage.getItem(
                    "bigBearJWTToken"
                  )}`,
                },
              }
            );

            if (!res.ok) throw new Error("Failed");
            setResendStatus((prev) => ({ ...prev, [bookingId]: "done" }));
            setTimeout(() => {
              setResendStatus((prev) => ({ ...prev, [bookingId]: "idle" }));
            }, 3000);
          } catch {
            setResendStatus((prev) => ({ ...prev, [bookingId]: "error" }));
            setTimeout(() => {
              setResendStatus((prev) => ({ ...prev, [bookingId]: "idle" }));
            }, 3000);
          }
        };

        return (
          <button
            onClick={resend}
            className="text-sm text-white bg-[#006838] hover:bg-green-800 px-3 py-1 rounded disabled:opacity-50"
            disabled={status === "sending"}
          >
            {status === "sending"
              ? "Sending..."
              : status === "done"
              ? "Sent!"
              : status === "error"
              ? "Failed"
              : "Resend Email"}
          </button>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
        <h1 className="text-2xl font-semibold text-[#006838]">Bookings</h1>
        <div className="flex gap-3 items-center">
          <input
            type="text"
            placeholder="Search guests..."
            className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#006838]"
            value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
            onChange={(e) =>
              table.getColumn("name")?.setFilterValue(e.target.value)
            }
          />
          <Link
            to="/admin/bookings/new"
            className="text-sm font-medium text-white bg-[#006838] hover:bg-green-800 transition px-4 py-2 rounded-md"
          >
            + Add Booking
          </Link>
        </div>
      </div>

      {isLoading && <p className="text-gray-500">Loading...</p>}
      {error instanceof Error && (
        <p className="text-red-600">{error.message}</p>
      )}
      {!isLoading && data.length === 0 && (
        <p className="text-gray-500 italic">No bookings found.</p>
      )}

      <div className="overflow-x-auto rounded-lg border">
        <table className="min-w-full border-collapse">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th key={header.id} className="px-4 py-3 border-b">
                    {header.isPlaceholder ? null : (
                      <div
                        className="cursor-pointer select-none flex items-center gap-1"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: "▲",
                          desc: "▼",
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white text-sm">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border-b hover:bg-gray-50">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-3 align-top">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
